'use client'

import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, Info, X } from 'lucide-react'

/**
 * Severity tier for a transient toast notification.
 * - `success` → olive accent, polite live-region announcement.
 * - `error`   → terra accent, assertive `role="alert"` announcement.
 * - `info`    → petrol accent, polite live-region announcement.
 */
type ToastType = 'success' | 'error' | 'info'

/** Internal toast record persisted in the provider's `useState` queue. */
interface Toast {
  /** Stable monotonic id used as React key + dismiss target. */
  id: number
  /** Plain-text body. HTML is not interpolated; toasts are not rich. */
  message: string
  /** Severity tier, drives icon + border + a11y semantics. */
  type: ToastType
}

/**
 * Context value exposed by {@link ToastProvider}, consumed via {@link useToast}.
 * Each method enqueues one toast at the given severity. Auto-dismiss is
 * handled internally; callers do not need to track ids.
 */
interface ToastContextValue {
  /** Enqueue a success toast (olive). Auto-dismisses after the default TTL. */
  success: (message: string) => void
  /** Enqueue an error toast (terra). Announced assertively to screen readers. */
  error: (message: string) => void
  /** Enqueue an info toast (petrol). Auto-dismisses after the default TTL. */
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let nextId = 0

// NB-F328: align toast palette with the rest of the design system
// (olive for success, terra for error, petrol for info) instead of the
// raw red/blue Tailwind defaults the primitive shipped with.
const CONFIG: Record<ToastType, { icon: React.ElementType; border: string; color: string }> = {
  success: { icon: CheckCircle, border: 'border-olive-500', color: 'text-olive-600' },
  error: { icon: XCircle, border: 'border-terra-500', color: 'text-terra-500' },
  info: { icon: Info, border: 'border-petrol-500', color: 'text-petrol-600' },
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = nextId++
    setToasts((prev) => [...prev, { id, message, type }])
  }, [])

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const value: ToastContextValue = {
    success: useCallback((m: string) => addToast(m, 'success'), [addToast]),
    error: useCallback((m: string) => addToast(m, 'error'), [addToast]),
    info: useCallback((m: string) => addToast(m, 'info'), [addToast]),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* NB-F328: aria-live=polite so SRs announce toasts without
          interrupting; success/info defer, errors still escalate via
          role=alert on the item itself. aria-atomic=false keeps
          announcements scoped to the new item only. */}
      <div
        role="region"
        aria-label="Notifications"
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      >
        <AnimatePresence>
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={() => remove(toast.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const { icon: Icon, border, color } = CONFIG[toast.type]
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t) }, [onDismiss])

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      role={toast.type === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg bg-cream-50 border ${border} shadow-lg min-w-[280px] max-w-sm`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${color}`} />
      <span className="text-sm text-ink-900 flex-1">{toast.message}</span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="text-ink-600 hover:text-ink-900 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

// Provider-optional variant for leaf components (e.g. gating prompts) that may
// be rendered outside the root ToastProvider in tests or isolated trees. In the
// real app the root layout always supplies the provider, so toasts surface
// normally; without one the calls are silent no-ops instead of a render crash.
const NOOP_TOAST: ToastContextValue = { success: () => {}, error: () => {}, info: () => {} }

export function useOptionalToast(): ToastContextValue {
  return useContext(ToastContext) ?? NOOP_TOAST
}
